import moment from 'moment-timezone';

import { IAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { QueryResponse as ActivityCodesByVersion } from 'api/queries/activityCodesByVersion.query';
import { QueryResponse as Clusters } from 'api/queries/clustersList.query';
import { QueryResponse as GetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';
import { QueryResponse as ResourceOptions } from 'api/queries/getResourceListForChart.query';
import { QueryResponse as GetWbs } from 'api/queries/getWbs.query';

import constants from 'components/common/Constants/constants';
import { FilterNames } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { options as partialCompletionCreditOptions } from 'components/common/GlobalFilterComponent/components/partialCompletionCreditFilter/partialCompletionCreditFilter';
import { productivityPreDefinedFilterOptions } from 'components/common/GlobalFilterComponent/components/productivityPreDefinedFilter/productivityPreDefinedFilter';
import { options as progressMeasurementOptions } from 'components/common/GlobalFilterComponent/components/progressMeasurementFilter/progressMeasurementFilter';
import { resourceTypeOptions } from 'components/common/GlobalFilterComponent/components/resourceTypeFilter/resourceTypeFilter';

import {
    activityTypeLabels,
    cardTypeLabels,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { statusOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import {
    activityCodesTag,
    milestoneFragnetTag,
    wbsTag,
    widgetFilterTitle,
} from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { IFilter, IFilterData } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TIME_FILTERS } from 'components/widgets/KeyAchievements/keyAchievementsTable.consts';
import { PRIORITIES_MATRIX_FILTERS } from 'components/widgets/PrioritiesMatrix/prioritiesMatrixComponent.utils';

export const generateFilterByType = (
    item: { name: FilterNames | string; data: IFilterData },
    //TODO: try to make IFilterData generic so will not need to check typeof in mappers
    // ( widgetFilterTitle,timePeriodTitle & numOfDaysToBeUrgentTitle
    activityCodes?: ActivityCodesByVersion[],
    milestoneFragnet?: GetMilestoneFragnet[],
    wbs?: GetWbs[],
    resourceOptions?: ResourceOptions[],
    assigneeOptionsList?: IAssigneeOptionsList[],
    clusters?: Clusters,
): IFilter => {
    switch (item.name) {
        case FilterNames.START_DATE:
        case FilterNames.FINISH_DATE:
        case FilterNames.DURATION:
            return { ...item, data: item.data?.map((date) => moment(date).format('MMM yyyy')) };
        case FilterNames.ACTIVITY_CODES:
        case FilterNames.ACTIVITY_CODES_EXCLUDE:
        case 'activityCodes':
        case 'activityCodesExclude':
        case 'ACTIVITY_CODE_CARD_BOARD':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return activityCodesTag({ item, activityCodes });
                }),
            };
        case FilterNames.FRAGNET_SEARCH:
        case FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD:
        case 'milestoneFragnet':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return milestoneFragnetTag({ item, milestoneFragnet });
                }),
            };
        case FilterNames.WBS_SEARCH:
        case 'wbs':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return wbsTag({ item, wbs });
                }),
            };
        case FilterNames.MIN_WBS_LEVEL:
        case FilterNames.MAX_WBS_LEVEL:
        case FilterNames.OPTIONS_STATUS:
        case FilterNames.OPTIONS_TYPE:
        case FilterNames.activity_type:
        case FilterNames.category:
        case FilterNames.severity:
            return { ...item, data: item.data?.map(widgetFilterTitle) };
        case FilterNames.NAME_SEARCH:
            return item;
        case FilterNames.TIME_PERIOD:
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        TIME_FILTERS.find((filter) => {
                            return filter.value === str;
                        })?.label || str
                    );
                }),
            };
        case FilterNames.NUM_OF_DAYS_TO_BE_URGENT:
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        PRIORITIES_MATRIX_FILTERS.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        case FilterNames.CRITICAL_PATH:
            return { ...item, data: [item.data && item.data[0] === 1 ? 'True' : 'False'] };
        case 'WBS_LEVEL_CARD_BOARD':
        case FilterNames.SUBMITTED_COMPLETION_CARD_BOARD:
            return { ...item, data: item.data && [`${item.data[0]} - ${item.data[1]}%`] };
        case FilterNames.PRODUCTIVITY_PRE_DEFINED:
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        productivityPreDefinedFilterOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        case 'progressMeasurement': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        progressMeasurementOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'partialCompletionCredit': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        partialCompletionCreditOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'resourceType': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        resourceTypeOptions.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'resource': {
            return {
                ...item,
                data: item.data?.map((str) => {
                    return (
                        resourceOptions?.find((filter) => {
                            return filter.value === str;
                        })?.label || ''
                    );
                }),
            };
        }
        case 'isCriticalPath': {
            return {
                ...item,
                data: item.data?.map((value) => {
                    return value ? 'True' : 'False';
                }),
            };
        }
        case 'START_DATE_CARD_BOARD':
        case 'END_DATE_CARD_BOARD':
        case 'COMMENT_CARD_BOARD':
        case 'BASELINE_START_DATE_CARD_BOARD':
        case 'BASELINE_END_DATE_CARD_BOARD':
            if (item.data.length === 0) {
                return { ...item, data: [] };
            }
            const newData = item.data
                .map((date) => moment(Number(date)).format(constants.formats.date.defaultForInput))
                .join(' - ');
            return { ...item, data: [newData] };
        case 'TRACKED_CARD_BOARD':
        case 'DOCLINK_CARD_BOARD':
        case 'DELAY_DRIVER_CARD_BOARD':
        case 'IMPORTANT_PREDECESSOR_OVERDUE':
        case 'RCF_RISK':
        case 'CONSTRAINT_DAY_SET':
        case 'LONG_DURATION':
        case 'IMPORTANT_LAG_CARD_30_BOARD':
        case 'OVERDUE_CARD_BOARD':
        case 'FINISH_PROG_OVERDUE_CARD_BOARD':
        case 'START_PROG_OVERDUE_CARD_BOARD':
        case 'SET_BACK':
        case 'IMPORTANT_STALLED':
        case 'CRITICAL_PATH_USER_CARD_BOARD':
            return { ...item, data: [item.data[0] === '1' ? 'Yes' : 'No'] };
        case 'UPDATED_USER_CARD_BOARD':
        case 'ASSIGNEE_CARD_BOARD':
            const assigneeFound = assigneeOptionsList?.find((assignee) => assignee.value === item.data[0]);
            return assigneeFound
                ? { ...item, data: [`${assigneeFound.assignee.firstName} ${assigneeFound.assignee.lastName}`] }
                : item;
        case 'DURATION_CARD_BOARD':
        case 'LAG_CARD_BOARD':
        case 'TOTAL_FLOAT_CARD_BOARD':
            return item;
        case 'ACTIVITY_TYPE_BOARD':
            return { ...item, data: item.data.map((i) => activityTypeLabels[i]) };
        case 'TYPE_CARD_BOARD':
            return { ...item, data: item.data.map((i) => cardTypeLabels[i]) };

        case 'TAG_CARD_BOARD':
        case 'TEXT_SEARCH_CARD_BOARD':
            return { ...item };
        case 'RESOURCE_NAME_CARD_BOARD':
            return { ...item, data: item.label };
        case 'STATUS_CARD_BOARD':
            return { ...item, data: item.data.map((i) => statusOptions.find((item) => item.name === i)?.label) };
        case 'CLUSTER_CARD_BOARD':
            const cluster = clusters?.clusterList.find((i) => i.clusterId === item.data[0])?.clusterName;
            return {
                ...item,
                data: cluster ? [cluster] : [],
            };
        default:
            return { ...item, data: item.data?.map(widgetFilterTitle) };
    }
};
